/**
 * Email Merge Tag Resolver
 *
 * Resolves merge tags in email templates like {{candidate.first_name}}, {{rfq.title}}, etc.
 * with actual values from provided data objects.
 *
 * Usage:
 *   const subject = resolveMergeTags(template.subject_template, { rfq, candidate });
 *   const body = resolveMergeTags(template.body_template, { rfq, candidate });
 */

/**
 * Format a date value for display in emails
 * @param {string|Date} dateValue - Date value to format
 * @returns {string} Formatted date string or 'Not specified'
 */
function formatDateForEmail(dateValue) {
  if (!dateValue) return 'Not specified';

  try {
    const date = new Date(dateValue);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateValue.toString();
  }
}

/**
 * Format an array value for display in emails
 * @param {Array} arrayValue - Array value to format
 * @returns {string} Comma-separated string or 'Not specified'
 */
function formatArrayForEmail(arrayValue) {
  if (!arrayValue || !Array.isArray(arrayValue) || arrayValue.length === 0) {
    return 'Not specified';
  }
  return arrayValue.join(', ');
}

/**
 * Get nested property value from object using dot notation
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot-notation path (e.g., 'client.name')
 * @returns {*} Value at path or null
 */
function getNestedValue(obj, path) {
  if (!obj || !path) return null;

  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return null;
    }
  }

  return value;
}

/**
 * Resolve all merge tags in a template string
 *
 * @param {string} template - Template string with merge tags like {{candidate.first_name}}
 * @param {Object} data - Data object containing values to replace merge tags
 * @param {Object} data.candidate - Candidate data (first_name, last_name, email, etc.)
 * @param {Object} data.rfq - RFQ data (ref_code, title, locations, etc.)
 * @param {Object} data.client - Client data (name, etc.) - can also be in rfq.client
 * @returns {string} Template with all merge tags resolved
 *
 * @example
 * const template = "Dear {{candidate.first_name}}, regarding {{rfq.title}}";
 * const data = {
 *   candidate: { first_name: 'John', last_name: 'Doe' },
 *   rfq: { ref_code: 'RFQ001', title: 'Software Engineer' }
 * };
 * const result = resolveMergeTags(template, data);
 * // Returns: "Dear John, regarding Software Engineer"
 */
export function resolveMergeTags(template, data = {}) {
  if (!template || typeof template !== 'string') {
    return template;
  }

  let resolved = template;

  // Extract all merge tags from template (matches {{anything}})
  const mergeTagPattern = /\{\{([^}]+)\}\}/g;
  const matches = template.match(mergeTagPattern);

  if (!matches) {
    return resolved; // No merge tags found
  }

  // Process each unique merge tag
  const uniqueTags = [...new Set(matches)];

  for (const tag of uniqueTags) {
    // Extract the path from the tag (e.g., 'candidate.first_name' from '{{candidate.first_name}}')
    const path = tag.replace('{{', '').replace('}}', '').trim();

    // Get the value for this path
    let value = getNestedValue(data, path);

    // Special handling for common patterns

    // RFQ client name can be nested or at root level
    if (path === 'rfq.client_name' && !value) {
      value = getNestedValue(data, 'rfq.client.name') || getNestedValue(data, 'client.name');
    }

    // Client name fallback
    if (path === 'client.name' && !value) {
      value = getNestedValue(data, 'rfq.client.name');
    }

    // Format dates
    if (path.includes('_date') || path.includes('_datetime')) {
      value = formatDateForEmail(value);
    }

    // Format arrays (like locations)
    if (Array.isArray(value)) {
      value = formatArrayForEmail(value);
    }

    // Replace null/undefined with empty string or 'Not specified' for certain fields
    if (value === null || value === undefined) {
      // For optional fields, use 'Not specified'
      if (path.includes('clearance') || path.includes('location') ||
          path.includes('work_arrangement') || path.includes('term') ||
          path.includes('start_date') || path.includes('close_date')) {
        value = 'Not specified';
      } else {
        value = ''; // For names and required fields, use empty string
      }
    }

    // Replace all occurrences of this merge tag with the value
    resolved = resolved.replace(new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
  }

  return resolved;
}

/**
 * Get all merge tags used in a template
 * Useful for validation and showing available merge tags to users
 *
 * @param {string} template - Template string
 * @returns {string[]} Array of merge tag paths (e.g., ['candidate.first_name', 'rfq.title'])
 */
export function extractMergeTags(template) {
  if (!template || typeof template !== 'string') {
    return [];
  }

  const mergeTagPattern = /\{\{([^}]+)\}\}/g;
  const matches = template.match(mergeTagPattern);

  if (!matches) {
    return [];
  }

  return [...new Set(matches.map(tag =>
    tag.replace('{{', '').replace('}}', '').trim()
  ))];
}

/**
 * Validate that all merge tags in a template can be resolved with given data
 *
 * @param {string} template - Template string
 * @param {Object} data - Data object
 * @returns {Object} Validation result { valid: boolean, missingTags: string[] }
 */
export function validateMergeTags(template, data = {}) {
  const tags = extractMergeTags(template);
  const missingTags = [];

  for (const tag of tags) {
    const value = getNestedValue(data, tag);
    if (value === null || value === undefined) {
      missingTags.push(tag);
    }
  }

  return {
    valid: missingTags.length === 0,
    missingTags
  };
}

/**
 * Escape HTML special characters to prevent XSS
 * @param {*} text - Text to escape
 * @returns {string} HTML-escaped text
 */
function escapeHtml(text) {
  if (typeof text !== 'string') {
    return text;
  }

  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };

  return text.replace(/[&<>"']/g, char => htmlEscapes[char]);
}

/**
 * Resolve all merge tags in an HTML template string
 * Similar to resolveMergeTags but escapes data values to prevent HTML injection
 * while preserving the template's HTML structure
 *
 * @param {string} templateHtml - HTML template string with merge tags
 * @param {Object} data - Data object containing values to replace merge tags
 * @returns {string} HTML template with all merge tags resolved and data values escaped
 *
 * @example
 * const template = "<p>Dear <strong>{{candidate.first_name}}</strong>, regarding {{rfq.title}}</p>";
 * const data = {
 *   candidate: { first_name: 'John<script>alert("XSS")</script>' },
 *   rfq: { title: 'Software Engineer' }
 * };
 * const result = resolveMergeTagsHtml(template, data);
 * // Returns: "<p>Dear <strong>John&lt;script&gt;alert(&quot;XSS&quot;)&lt;/script&gt;</strong>, regarding Software Engineer</p>"
 */
export function resolveMergeTagsHtml(templateHtml, data = {}) {
  if (!templateHtml || typeof templateHtml !== 'string') {
    return templateHtml;
  }

  let resolved = templateHtml;

  // Extract all merge tags from template
  const mergeTagPattern = /\{\{([^}]+)\}\}/g;
  const matches = templateHtml.match(mergeTagPattern);

  if (!matches) {
    return resolved; // No merge tags found
  }

  // Process each unique merge tag
  const uniqueTags = [...new Set(matches)];

  for (const tag of uniqueTags) {
    // Extract the path from the tag
    const path = tag.replace('{{', '').replace('}}', '').trim();

    // Get the value for this path
    let value = getNestedValue(data, path);

    // Special handling for common patterns (same as resolveMergeTags)

    // RFQ client name can be nested or at root level
    if (path === 'rfq.client_name' && !value) {
      value = getNestedValue(data, 'rfq.client.name') || getNestedValue(data, 'client.name');
    }

    // Client name fallback
    if (path === 'client.name' && !value) {
      value = getNestedValue(data, 'rfq.client.name');
    }

    // Format dates
    if (path.includes('_date') || path.includes('_datetime')) {
      value = formatDateForEmail(value);
    }

    // Format arrays (like locations)
    if (Array.isArray(value)) {
      value = formatArrayForEmail(value);
    }

    // Replace null/undefined with empty string or 'Not specified' for certain fields
    if (value === null || value === undefined) {
      // For optional fields, use 'Not specified'
      if (path.includes('clearance') || path.includes('location') ||
          path.includes('work_arrangement') || path.includes('term') ||
          path.includes('start_date') || path.includes('close_date')) {
        value = 'Not specified';
      } else {
        value = ''; // For names and required fields, use empty string
      }
    }

    // HTML-escape the value to prevent injection
    const escapedValue = escapeHtml(String(value));

    // Replace all occurrences of this merge tag with the escaped value
    resolved = resolved.replace(
      new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      escapedValue
    );
  }

  return resolved;
}

/**
 * Available merge tags by context
 * Useful for showing hints in UI
 */
export const AVAILABLE_MERGE_TAGS = {
  candidate: [
    { tag: '{{candidate.first_name}}', description: 'Candidate first name' },
    { tag: '{{candidate.last_name}}', description: 'Candidate last name' },
    { tag: '{{candidate.email}}', description: 'Candidate email address' },
    { tag: '{{candidate.location_state}}', description: 'Candidate state/location' },
    { tag: '{{candidate.clearance_level}}', description: 'Candidate clearance level' },
  ],
  rfq: [
    { tag: '{{rfq.ref_code}}', description: 'RFQ reference code' },
    { tag: '{{rfq.title}}', description: 'RFQ position title' },
    { tag: '{{rfq.client_name}}', description: 'Client organization name' },
    { tag: '{{rfq.locations}}', description: 'RFQ locations (comma-separated)' },
    { tag: '{{rfq.clearance_required}}', description: 'Required clearance level' },
    { tag: '{{rfq.work_arrangement}}', description: 'Work arrangement (remote/onsite/hybrid)' },
    { tag: '{{rfq.client_close_datetime}}', description: 'RFQ closing date' },
    { tag: '{{rfq.estimated_start_date}}', description: 'Estimated start date' },
    { tag: '{{rfq.initial_term_months}}', description: 'Initial contract term (months)' },
  ],
  client: [
    { tag: '{{client.name}}', description: 'Client organization name' },
  ]
};
